import { useState, useEffect, useRef } from "react";
import api, { woocommerceAPI, tiktokAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { SpedisciSettingsCard } from "@/components/SpedisciSettingsCard";
import {
  MessageCircle,
  ShoppingBag,
  Store,
  Settings,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ExternalLink,
  Key,
  Webhook,
  Copy,
  Download,
  Plus,
  Trash2,
  Upload,
  FileText,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Integrations = () => {
  const [integrations, setIntegrations] = useState({
    whatsapp: { enabled: false, api_key: "", phone_number_id: "", access_token: "" },
    tiktok: { enabled: false, app_key: "", app_secret: "", shop_id: "" },
    woocommerce: { enabled: false, site_url: "", consumer_key: "", consumer_secret: "" },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [wooStatus, setWooStatus] = useState(null);
  
  // TikTok manual import
  const [showTikTokImport, setShowTikTokImport] = useState(false);
  const [tiktokOrders, setTiktokOrders] = useState([{ code: "", format: "100ml", amount: "" }]);
  const [importingTikTok, setImportingTikTok] = useState(false);
  const [tiktokResult, setTiktokResult] = useState(null);
  const tiktokFileRef = useRef(null);

  useEffect(() => {
    loadIntegrations();
    checkWooConnection();
    // Generate webhook URL
    const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    setWebhookUrl(`${baseUrl}/api/webhooks/orders`);
  }, []);

  const checkWooConnection = async () => {
    try {
      const res = await woocommerceAPI.test();
      setWooStatus(res.data);
    } catch {
      setWooStatus({ success: false, message: "Non configurato" });
    }
  };

  const importWooOrders = async () => {
    setImporting(true);
    try {
      const res = await woocommerceAPI.importOrders();
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error("Errore durante l'importazione");
    } finally {
      setImporting(false);
    }
  };

  const loadIntegrations = async () => {
    try {
      const res = await api.get("/integrations");
      if (res.data) {
        setIntegrations(prev => ({
          ...prev,
          ...res.data,
        }));
      }
    } catch (error) {
      // Integrations not configured yet, use defaults
    } finally {
      setLoading(false);
    }
  };

  const saveIntegration = async (type) => {
    setSaving(type);
    try {
      await api.post(`/integrations/${type}`, integrations[type]);
      toast.success(`Integrazione ${type} salvata con successo!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(null);
    }
  };

  const testConnection = async (type) => {
    try {
      const res = await api.post(`/integrations/${type}/test`);
      if (res.data.success) {
        toast.success(`Connessione ${type} riuscita!`);
      } else {
        toast.error(`Connessione ${type} fallita: ${res.data.message}`);
      }
    } catch (error) {
      toast.error("Errore nel test della connessione");
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success("URL copiato negli appunti!");
  };

  // TikTok manual import functions
  const addTikTokOrder = () => {
    setTiktokOrders([...tiktokOrders, { code: "", format: "100ml", amount: "" }]);
  };

  const removeTikTokOrder = (index) => {
    setTiktokOrders(tiktokOrders.filter((_, i) => i !== index));
  };

  const updateTikTokOrder = (index, field, value) => {
    const updated = [...tiktokOrders];
    updated[index][field] = value;
    setTiktokOrders(updated);
  };

  const importTikTokOrders = async () => {
    // Validate orders
    const validOrders = tiktokOrders.filter(o => o.code && o.amount);
    if (validOrders.length === 0) {
      toast.error("Inserisci almeno un ordine valido");
      return;
    }

    setImportingTikTok(true);
    try {
      const token = localStorage.getItem("token");
      
      for (const order of validOrders) {
        // Create order via API
        await api.post("/orders", {
          customer_name: "Cliente TikTok",
          customer_address: "TikTok Shop",
          channel: "tiktok_shop",
          items: [{
            perfume_id: order.code,
            format: order.format,
          }],
          total_amount: parseFloat(order.amount),
          courier: "GLS",
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast.success(`${validOrders.length} ordini TikTok importati!`);
      setShowTikTokImport(false);
      setTiktokOrders([{ code: "", format: "100ml", amount: "" }]);
    } catch (error) {
      toast.error("Errore nell'import: " + (error.response?.data?.detail || error.message));
    } finally {
      setImportingTikTok(false);
    }
  };

  const handleTikTokFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.pdf')) {
      toast.error("Il file deve essere un PDF");
      return;
    }

    setImportingTikTok(true);
    setTiktokResult(null);

    try {
      const res = await tiktokAPI.importPicking(file);
      setTiktokResult(res.data);
      toast.success(`Importati ${res.data.total_items} prodotti da TikTok!`);
    } catch (error) {
      toast.error("Errore: " + (error.response?.data?.detail || error.message));
    } finally {
      setImportingTikTok(false);
      if (tiktokFileRef.current) {
        tiktokFileRef.current.value = "";
      }
    }
  };

  const updateIntegration = (type, field, value) => {
    setIntegrations(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
      },
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#C9A227] animate-pulse font-medium">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="integrations-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-serif text-gray-900 mb-2">Integrazioni</h1>
        <p className="text-gray-500">
          Collega i tuoi canali di vendita per sincronizzare automaticamente gli ordini
        </p>
      </div>

      <SpedisciSettingsCard />

      {/* Webhook URL Card */}
      <Card className="bg-gradient-to-r from-amber-50 to-amber-100 border-[#C9A227]/30">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-white rounded-xl shadow-sm">
              <Webhook className="w-6 h-6 text-[#C9A227]" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-1">Webhook URL per Ordini</h3>
              <p className="text-sm text-gray-600 mb-3">
                Usa questo URL per ricevere notifiche automatiche quando arrivano nuovi ordini.
                Il fatturato in "Tempo Reale" si aggiornerà automaticamente.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-white rounded-lg text-sm text-gray-700 border border-gray-200 truncate">
                  {webhookUrl}
                </code>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyWebhookUrl}
                  className="border-[#C9A227] text-[#C9A227] hover:bg-amber-50"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Integration Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* WhatsApp Business */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-50 rounded-xl">
                  <MessageCircle className="w-6 h-6 text-emerald-500" />
                </div>
                <div>
                  <CardTitle className="text-gray-900 text-lg">WhatsApp Business</CardTitle>
                  <p className="text-xs text-gray-500">Meta Business API</p>
                </div>
              </div>
              <Switch
                checked={integrations.whatsapp.enabled}
                onCheckedChange={(checked) => updateIntegration("whatsapp", "enabled", checked)}
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-700 text-sm font-medium">Phone Number ID</Label>
              <Input
                type="text"
                value={integrations.whatsapp.phone_number_id}
                onChange={(e) => updateIntegration("whatsapp", "phone_number_id", e.target.value)}
                placeholder="1234567890"
                className="bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 text-sm font-medium">Access Token</Label>
              <Input
                type="password"
                value={integrations.whatsapp.access_token}
                onChange={(e) => updateIntegration("whatsapp", "access_token", e.target.value)}
                placeholder="EAAxxxxxxx..."
                className="bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => saveIntegration("whatsapp")}
                disabled={saving === "whatsapp"}
                className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600"
              >
                {saving === "whatsapp" ? "Salvataggio..." : "Salva"}
              </Button>
              <Button
                variant="outline"
                onClick={() => testConnection("whatsapp")}
                className="border-gray-200"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            <a
              href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-emerald-600 hover:underline"
            >
              <ExternalLink className="w-3 h-3" />
              Guida configurazione
            </a>
          </CardContent>
        </Card>

        {/* TikTok Shop - Import Picking List */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-pink-50 rounded-xl">
                  <ShoppingBag className="w-6 h-6 text-pink-500" />
                </div>
                <div>
                  <CardTitle className="text-gray-900 text-lg">TikTok Shop</CardTitle>
                  <p className="text-xs text-gray-500">Import da Picking List PDF</p>
                </div>
              </div>
              <Badge className="bg-pink-100 text-pink-700">PDF Import</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Carica la picking list PDF scaricata da TikTok Seller Center. 
              Il sistema estrae automaticamente codici, ML e quantità.
            </p>
            
            <input
              type="file"
              accept=".pdf"
              ref={tiktokFileRef}
              onChange={handleTikTokFileUpload}
              className="hidden"
            />
            
            <Button
              onClick={() => tiktokFileRef.current?.click()}
              disabled={importingTikTok}
              className="w-full bg-pink-500 text-white hover:bg-pink-600"
            >
              {importingTikTok ? (
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Analisi in corso...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Carica Picking List PDF
                </span>
              )}
            </Button>

            {/* Result display */}
            {tiktokResult && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200 space-y-3">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="font-semibold">Import completato!</span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-white p-2 rounded">
                    <p className="text-2xl font-bold text-pink-600">{tiktokResult.total_items}</p>
                    <p className="text-xs text-gray-500">Prodotti</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-2xl font-bold text-pink-600">{tiktokResult.unique_codes}</p>
                    <p className="text-xs text-gray-500">Codici</p>
                  </div>
                  <div className="bg-white p-2 rounded">
                    <p className="text-2xl font-bold text-pink-600">€{tiktokResult.total_revenue}</p>
                    <p className="text-xs text-gray-500">Fatturato</p>
                  </div>
                </div>
                <p className="text-xs text-green-600">
                  Stock aggiornato per {tiktokResult.stock_updated} prodotti
                </p>
              </div>
            )}

            <div className="p-3 bg-pink-50 rounded-lg">
              <p className="text-xs text-pink-700">
                <strong>Come funziona:</strong> Scarica la picking list dal TikTok Seller Center, 
                caricala qui e il sistema calcola automaticamente fatturato e scala lo stock.
              </p>
            </div>

            {/* Manual import button as fallback */}
            <Button
              variant="outline"
              onClick={() => setShowTikTokImport(true)}
              className="w-full border-gray-200 text-gray-600"
            >
              <FileText className="w-4 h-4 mr-2" />
              Inserimento Manuale
            </Button>
          </CardContent>
        </Card>

        {/* WooCommerce */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-50 rounded-xl">
                  <Store className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <CardTitle className="text-gray-900 text-lg">WooCommerce</CardTitle>
                  <p className="text-xs text-gray-500">WordPress Plugin</p>
                </div>
              </div>
              <Switch
                checked={integrations.woocommerce.enabled}
                onCheckedChange={(checked) => updateIntegration("woocommerce", "enabled", checked)}
              />
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-700 text-sm font-medium">URL Sito</Label>
              <Input
                type="url"
                value={integrations.woocommerce.site_url}
                onChange={(e) => updateIntegration("woocommerce", "site_url", e.target.value)}
                placeholder="https://tuosito.com"
                className="bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 text-sm font-medium">Consumer Key</Label>
              <Input
                type="text"
                value={integrations.woocommerce.consumer_key}
                onChange={(e) => updateIntegration("woocommerce", "consumer_key", e.target.value)}
                placeholder="ck_xxxxxxxx"
                className="bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-700 text-sm font-medium">Consumer Secret</Label>
              <Input
                type="password"
                value={integrations.woocommerce.consumer_secret}
                onChange={(e) => updateIntegration("woocommerce", "consumer_secret", e.target.value)}
                placeholder="cs_xxxxxxxx"
                className="bg-gray-50 border-gray-200 text-gray-900"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={() => saveIntegration("woocommerce")}
                disabled={saving === "woocommerce"}
                className="flex-1 bg-purple-500 text-white hover:bg-purple-600"
              >
                {saving === "woocommerce" ? "Salvataggio..." : "Salva"}
              </Button>
              <Button
                variant="outline"
                onClick={() => testConnection("woocommerce")}
                className="border-gray-200"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            
            {/* WooCommerce Status & Import */}
            {wooStatus?.success && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span className="font-medium text-green-700">WooCommerce Connesso</span>
                </div>
                <Button
                  onClick={importWooOrders}
                  disabled={importing}
                  className="w-full bg-green-500 hover:bg-green-600 text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {importing ? "Importazione in corso..." : "Importa Ordini da WooCommerce"}
                </Button>
                <p className="text-xs text-green-600 mt-2">
                  Importa tutti gli ordini non ancora presenti nel sistema
                </p>
              </div>
            )}
            
            <a
              href="https://woocommerce.github.io/woocommerce-rest-api-docs/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-purple-600 hover:underline"
            >
              <ExternalLink className="w-3 h-3" />
              Documentazione WooCommerce API
            </a>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="p-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#C9A227]" />
            Come Funziona la Sincronizzazione
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-sm">
                1
              </div>
              <div>
                <p className="font-medium text-gray-900">Configura le API</p>
                <p className="text-sm text-gray-500">
                  Inserisci le credenziali dei tuoi canali di vendita
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                2
              </div>
              <div>
                <p className="font-medium text-gray-900">Attiva il Webhook</p>
                <p className="text-sm text-gray-500">
                  Copia l'URL webhook nelle impostazioni di ogni piattaforma
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 font-bold text-sm">
                3
              </div>
              <div>
                <p className="font-medium text-gray-900">Ordini in Tempo Reale</p>
                <p className="text-sm text-gray-500">
                  Gli ordini arriveranno automaticamente e vedrai i soldi aumentare!
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* TikTok Import Dialog */}
      <Dialog open={showTikTokImport} onOpenChange={setShowTikTokImport}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-pink-500" />
              Import Ordini TikTok
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Inserisci il codice profumo, formato e importo pagato per ogni ordine.
            </p>

            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {tiktokOrders.map((order, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Input
                    placeholder="Codice (es. G46)"
                    value={order.code}
                    onChange={(e) => updateTikTokOrder(index, "code", e.target.value)}
                    className="w-24"
                  />
                  <Select
                    value={order.format}
                    onValueChange={(v) => updateTikTokOrder(index, "format", v)}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30ml">30ml</SelectItem>
                      <SelectItem value="50ml">50ml</SelectItem>
                      <SelectItem value="100ml">100ml</SelectItem>
                      <SelectItem value="200ml">200ml</SelectItem>
                      <SelectItem value="500ml">500ml</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="€"
                    type="number"
                    value={order.amount}
                    onChange={(e) => updateTikTokOrder(index, "amount", e.target.value)}
                    className="w-20"
                  />
                  {tiktokOrders.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTikTokOrder(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <Button
              variant="outline"
              onClick={addTikTokOrder}
              className="w-full border-dashed"
            >
              <Plus className="w-4 h-4 mr-2" />
              Aggiungi Ordine
            </Button>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowTikTokImport(false)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={importTikTokOrders}
                disabled={importingTikTok}
                className="flex-1 bg-pink-500 text-white hover:bg-pink-600"
              >
                {importingTikTok ? "Importazione..." : `Importa ${tiktokOrders.filter(o => o.code && o.amount).length} Ordini`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Integrations;
